import { api } from '../api/client'

/**
 * Tilmelding til push-notifikationer (beslutning 032).
 *
 * Browserens PushManager arbejder med binære nøgler og et Notification.permission
 * der ikke kan trækkes tilbage fra kode. Alt det rod holdes her, så
 * brugerfladen kun skal forholde sig til "til" og "fra".
 */

export type PushState =
  | 'unsupported' // Browseren kan ikke push
  | 'ios-needs-install' // iPhone: virker kun når appen ligger på hjemmeskærmen
  | 'denied' // Brugeren har sagt nej - kan kun ændres i browserens indstillinger
  | 'off' // Kan tilmeldes
  | 'on' // Tilmeldt

/** Er push overhovedet muligt her? */
export function isSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/**
 * iOS understøtter Web Push fra 16.4, men kun når siden er lagt på
 * hjemmeskærmen - ikke i en Safari-fane. Det er den hyppigste årsag til, at
 * "det virker ikke på min iPhone", så det skal kunne siges tydeligt.
 */
export function isIosWithoutInstall() {
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent)
  if (!ios) return false

  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS' egen, ikke-standardiserede markør.
    (window.navigator as { standalone?: boolean }).standalone === true

  return !standalone
}

/** Hvor står vi lige nu? */
export async function getState(): Promise<PushState> {
  if (!isSupported()) return isIosWithoutInstall() ? 'ios-needs-install' : 'unsupported'
  if (isIosWithoutInstall()) return 'ios-needs-install'
  if (Notification.permission === 'denied') return 'denied'

  const registration = await navigator.serviceWorker.getRegistration()
  const subscription = await registration?.pushManager.getSubscription()

  return subscription ? 'on' : 'off'
}

/**
 * Beder om lov og tilmelder browseren. Kaster med en dansk besked, hvis noget
 * går galt - kalderen viser den bare.
 */
export async function enable(): Promise<void> {
  if (!isSupported()) {
    throw new Error('Denne browser understøtter ikke push-beskeder.')
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error(
      'Du sagde nej til beskeder. Det kan slås til igen under indstillingerne for siden i din browser.'
    )
  }

  const registration = await navigator.serviceWorker.ready
  const { publicKey } = await api.get<{ publicKey: string; subscriptionCount: number }>('/api/push/key')

  // Er der allerede et abonnement med en anden nøgle, skal det væk først -
  // ellers afviser browseren tilmeldingen.
  const existing = await registration.pushManager.getSubscription()
  if (existing) {
    await existing.unsubscribe()
  }

  const subscription = await registration.pushManager.subscribe({
    // Uden denne er beskederne synlige for push-tjenesten. Chrome kræver den.
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey)
  })

  const json = subscription.toJSON()

  await api.post('/api/push/subscriptions', {
    endpoint: subscription.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
    userAgent: navigator.userAgent
  })
}

/** Afmelder denne browser. Tilladelsen bliver stående - den kan kun ændres i browseren. */
export async function disable(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration()
  const subscription = await registration?.pushManager.getSubscription()

  if (subscription) {
    await api.delete(`/api/push/subscriptions?endpoint=${encodeURIComponent(subscription.endpoint)}`)
    await subscription.unsubscribe()
  }
}

/** Sender en prøvebesked til brugerens egne telefoner. */
export function sendTest() {
  return api.post<{ delivered: number }>('/api/push/test')
}

/**
 * VAPID-nøglen kommer som base64url-tekst, men PushManager vil have bytes.
 * Base64url bruger - og _ hvor almindelig base64 bruger + og /, og
 * udfyldningstegnene er skåret væk - begge dele skal sættes tilbage.
 */
function urlBase64ToUint8Array(base64Url: string) {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4)
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)

  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)))
}
